import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

/**
 * Interceptor to capture API responses with success: false pattern
 * Detects server-side logical errors that return 200 OK but indicate failure in response body
 */
@Injectable()
export class SentryApiErrorInterceptor implements HttpInterceptor {
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Capture authorization header context for Sentry debugging
    // This bypasses Sentry's default header scrubbing
    this.captureAuthContext(request);

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        // Only check successful HTTP responses
        if (
          event instanceof HttpResponse &&
          event.status >= 200 &&
          event.status < 300
        ) {
          this.checkForApiError(event, request);
        }
      })
    );
  }

  /**
   * Captures authorization header as Sentry context to bypass server-side scrubbing
   */
  private captureAuthContext(request: HttpRequest<unknown>): void {
    if (typeof Sentry === 'undefined' || !Sentry.setContext) {
      return;
    }

    const authHeader = request.headers.get('Authorization');
    if (authHeader) {
      Sentry.setContext('auth_request', {
        url: request.url,
        method: request.method,
        authHeader: authHeader,
        authType: authHeader.split(' ')[0], // e.g., "Bearer"
        timestamp: new Date().toISOString(),
      });
    }
  }

  private checkForApiError(
    response: HttpResponse<any>,
    request: HttpRequest<unknown>
  ): void {
    const body = response.body;

    // Check if response body indicates an API error
    if (body && typeof body === 'object' && body.success === false) {
      // Capture this as an error in Sentry
      if (typeof Sentry !== 'undefined' && Sentry.captureException) {
        const errorMessage = body.message || 'API returned success: false';
        const errorCode = body.errorCode || body.code || 'no-code';

        Sentry.withScope((scope) => {
          // Enhanced tagging for better filtering
          scope.setTag('error_type', 'api_logical_error');
          scope.setTag('http_status', response.status.toString());
          scope.setTag('http_method', request.method);

          // Extract and tag the API endpoint path (without query params)
          try {
            const url = new URL(request.url);
            scope.setTag('api_endpoint', url.pathname);
            scope.setTag('api_host', url.host);
          } catch (e) {
            scope.setTag('api_endpoint', request.url);
          }

          // Tag error code if available
          if (errorCode !== 'no-code') {
            scope.setTag('error_code', errorCode.toString());
          }

          // Set error level based on severity
          if (
            errorMessage.toLowerCase().includes('fatal') ||
            errorMessage.toLowerCase().includes('critical')
          ) {
            scope.setLevel('error');
          } else {
            scope.setLevel('warning');
          }

          // Add detailed context for debugging
          scope.setContext('api_request', {
            url: request.url,
            method: request.method,
            body: request.body,
          });

          scope.setContext('api_response', {
            status: response.status,
            success: body.success,
            message: body.message,
            errorCode: errorCode,
            responseBody: body,
          });

          // Create an error from the API response
          const apiError = new Error(`API Error: ${errorMessage}`);
          apiError.name = 'ApiLogicalError';

          // Proper fingerprint for grouping similar errors
          // This groups errors by: type, method, endpoint, message, and error code
          try {
            const url = new URL(request.url);
            scope.setFingerprint([
              'api-logical-error',
              request.method,
              url.pathname, // Use pathname only (no query params or domain)
              errorMessage,
              errorCode.toString(),
            ]);
          } catch (e) {
            // Fallback if URL parsing fails
            scope.setFingerprint([
              'api-logical-error',
              request.method,
              request.url,
              errorMessage,
              errorCode.toString(),
            ]);
          }

          Sentry.captureException(apiError);
        });
      }
    }
  }
}
